import { useEffect, useState } from "react";
import type { BrowseTrack } from "@touchscreen/api-client";
import {
  useRequestTrack,
  useTouchscreenContext,
  useSearch,
  useVenueState,
  requestErrorMessage,
  searchEmptyStateMessage,
} from "@touchscreen/hooks";
import { OnScreenKeyboard } from "../components/OnScreenKeyboard.js";
import { Tile } from "../components/Tile.js";
import { ConfirmRequestModal } from "../components/ConfirmRequestModal.js";
import { RequestConfirmedOverlay } from "../components/RequestConfirmedOverlay.js";
import { artworkUrl } from "../lib/artwork.js";
import "./SearchScreen.css";

export interface SearchScreenProps {
  onBack: () => void;
  onRequested: () => void;
}

export function SearchScreen(props: SearchScreenProps): React.JSX.Element {
  const { venue } = useTouchscreenContext();
  const { requestsDisabled } = useVenueState();
  const { state: requestState, requestTrack, isCoolingDown, reset } = useRequestTrack();

  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [selectedTrack, setSelectedTrack] = useState<BrowseTrack | null>(null);
  const [confirmedTrack, setConfirmedTrack] = useState<BrowseTrack | null>(null);

  const searchEnabled = venue?.search.enabled ?? false;
  const strategy = venue?.search.strategy ?? "catalogue";

  // Debounce typing from the on-screen keyboard before it becomes a query key.
  // 1s because typing on the touchscreen's on-screen keyboard is slow, so a shorter
  // debounce was firing a search request on nearly every keystroke.
  useEffect(() => {
    const handle = setTimeout(() => setDebouncedQuery(query), 1000);
    return () => clearTimeout(handle);
  }, [query]);

  const { data: page, isFetching: loading } = useSearch(debouncedQuery, { enabled: searchEnabled, perPage: 40 });
  const results = debouncedQuery.trim() ? page?.items ?? null : null;

  function handleConfirm(): void {
    if (!selectedTrack) return;
    const track = selectedTrack;
    requestTrack(track.sm_id).then((result) => {
      if (result) {
        reset();
        setSelectedTrack(null);
        setConfirmedTrack(track);
        setTimeout(() => {
          setConfirmedTrack(null);
          props.onRequested();
        }, 1500);
      }
    });
  }

  if (!searchEnabled) {
    return (
      <div className="search-screen search-screen--message">
        <p>Search isn't available at this venue.</p>
        <button onClick={props.onBack}>Back</button>
      </div>
    );
  }

  return (
    <div className="search-screen">
      <header className="search-screen__header">
        <button className="search-screen__back" onClick={props.onBack}>
          ← Back
        </button>
        <div className="search-screen__query">{query || "Search for a track…"}</div>
      </header>

      <div className="search-screen__results">
        {loading ? <p className="search-screen__hint">Searching…</p> : null}
        {!loading && results !== null && results.length === 0 ? (
          <p className="search-screen__hint">{searchEmptyStateMessage(strategy)}</p>
        ) : null}
        <div className="search-screen__grid">
          {(results ?? []).map((track, index) => (
            <Tile
              // sm_id alone isn't a safe key: the same track can appear more than once in results.
              key={`${track.sm_id}-${index}`}
              title={track.title}
              subtitle={track.artist}
              artworkUrl={track.artwork_ref ?? artworkUrl(track.sm_id)}
              onClick={requestsDisabled ? undefined : () => setSelectedTrack(track)}
            />
          ))}
        </div>
      </div>

      <OnScreenKeyboard
        onKey={(char) => setQuery((q) => q + char)}
        onSpace={() => setQuery((q) => `${q} `)}
        onBackspace={() => setQuery((q) => q.slice(0, -1))}
        onClear={() => setQuery("")}
      />

      {!requestsDisabled && selectedTrack ? (
        <ConfirmRequestModal
          track={selectedTrack}
          submitting={requestState.status === "loading"}
          disabledReason={isCoolingDown(selectedTrack.sm_id) ? "This track was just requested" : null}
          errorMessage={requestState.status === "error" ? requestErrorMessage(requestState.error) : null}
          onConfirm={handleConfirm}
          onCancel={() => {
            setSelectedTrack(null);
            reset();
          }}
        />
      ) : null}

      {confirmedTrack ? <RequestConfirmedOverlay track={confirmedTrack} /> : null}
    </div>
  );
}
